// DEM-2079 (#3 visibilidade) — interpretação do motivo REAL de bloqueio de uma
// demand. Extraído pra módulo próprio (testável em isolamento, como
// demand-step-info.ts) porque a lógica é o coração da correção: a UI mostrava um
// texto HARDCODED ("Pipeline falhou no decomposer") que não tinha relação com a
// causa. Aqui traduzimos os códigos-máquina (reason_code / decision_reason) pra
// PT-BR legível e montamos um título claro de ONDE bloqueou; o objeto cru
// (blocked.raw) fica pro "Detalhes técnicos" colapsável da UI.

import type { DemandBlockedInfo } from '@/lib/api';

/** reason_code (código-máquina do agente/erro) → frase PT-BR interpretada. */
const REASON_CODE_PT: Record<string, string> = {
  agent_output_status_not_ok:
    'o agente retornou status não-OK — a própria saída foi marcada como bloqueada/reprovada',
  agent_execution_failed: 'a execução do agente falhou',
  agent_runner_llm_threw: 'o provedor de LLM lançou um erro durante a execução do agente',
  output_tasks_not_array: 'o decompositor não devolveu a lista de tasks no formato esperado',
  non_convergence: 'o loop de revisão não convergiu (limite de idas e voltas atingido)',
  authentication_error: 'falha de autenticação com o provedor de LLM (credencial da conta)',
};

/** decision_reason (decisão do pipeline) → frase PT-BR interpretada. */
const DECISION_REASON_PT: Record<string, string> = {
  agent_output_blocked_no_retry: 'o agente bloqueou a saída e não havia retry aplicável',
  backward_hard_ceiling: 'o teto de retornos ao desenvolvimento foi atingido',
  non_convergence: 'o consenso não convergiu após o limite de rodadas',
};

/** Capitaliza o slug do agente pra exibição (bezalel → Bezalel). */
function prettyAgent(slug: string | null): string | null {
  if (!slug) return null;
  return slug.charAt(0).toUpperCase() + slug.slice(1);
}

/** Transforma um slug (peer-review / demand_refinement) em rótulo legível. */
function prettySlug(slug: string): string {
  return slug
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (ch) => ch.toUpperCase());
}

/** Descreve ONDE bloqueou usando SEMPRE o número 1-based (step_number), nunca o
 *  step_index cru. Ex.: "step 5 (Peer Review)". */
function describeStep(blocked: DemandBlockedInfo): string | null {
  if (blocked.step_number != null && blocked.step_slug) {
    return `step ${blocked.step_number} (${prettySlug(blocked.step_slug)})`;
  }
  if (blocked.step_number != null) return `step ${blocked.step_number}`;
  if (blocked.step_slug) return prettySlug(blocked.step_slug);
  return null;
}

export interface InterpretedBlock {
  /** Título claro de ONDE bloqueou (ex.: "Bloqueada no step 5 (Peer Review) — Jeremias"). */
  title: string;
  /** Motivo interpretado em PT-BR (ex.: "o agente retornou status não-OK…"). */
  reason: string | null;
  /** true quando há objeto cru pra mostrar no "Detalhes técnicos" colapsável. */
  hasTechnicalDetail: boolean;
}

/**
 * Interpreta o motivo de bloqueio estruturado em título + motivo legíveis.
 * NUNCA joga; campos ausentes degradam graciosamente (título só "Bloqueada",
 * motivo null). A tradução tenta, em ordem: reason_code conhecido →
 * decision_reason conhecido → mensagem técnica → o próprio código cru.
 */
export function interpretBlocked(blocked: DemandBlockedInfo): InterpretedBlock {
  const step = describeStep(blocked);
  const agent = prettyAgent(blocked.agent);

  let where: string | null = null;
  if (step && agent) where = `no ${step} — ${agent}`;
  else if (step) where = `no ${step}`;
  else if (agent) where = `pelo agente ${agent}`;

  const title = where ? `Bloqueada ${where}` : 'Bloqueada';

  const reason =
    (blocked.reason_code ? REASON_CODE_PT[blocked.reason_code] : undefined) ??
    (blocked.decision_reason ? DECISION_REASON_PT[blocked.decision_reason] : undefined) ??
    blocked.message ??
    blocked.reason_code ??
    blocked.decision_reason ??
    null;

  const hasTechnicalDetail =
    !!blocked.raw && typeof blocked.raw === 'object' && Object.keys(blocked.raw).length > 0;

  return { title, reason, hasTechnicalDetail };
}
